import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PortalHeader, PortalLoading } from '../components/PortalComponents';
import { Button } from '../components/ui/button';
import { ScrollToTop } from '../components/ScrollToTop';
import { CustomerService } from '../services/mockServices';
import { SecuritySettings } from '../components/SecuritySettings';
import { toast } from '../components/ui/sonner';
import { 
  User, Mail, Phone, MapPin, 
  Edit2, LogOut, Shield, Droplets, Leaf, Award, Star
} from 'lucide-react';

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: ''
    }
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const data = await CustomerService.getProfile(user?.id);
        setProfile(data);
        setFormData({
          phone: data.phone,
          address: { ...data.address }
        });
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSave = async () => {
    try {
      await CustomerService.updateProfile(user?.id, formData);
      setProfile(prev => ({
        ...prev,
        phone: formData.phone,
        address: formData.address
      }));
      setEditing(false);
      toast.success('Perfil actualizado correctamente');
    } catch (err) {
      toast.error('Error al actualizar el perfil');
    }
  };

  if (loading) {
    return <PortalLoading />;
  }

  // Simulated impact data (would come from backend based on customer tenure)
  const customerSinceDate = new Date(profile?.customerSince || '2024-01-15');
  const monthsAsCustomer = Math.max(1, Math.floor((new Date() - customerSinceDate) / (1000 * 60 * 60 * 24 * 30)));
  const litersFiltered = monthsAsCustomer * 350; // ~350L/month estimate
  const bottlesAvoided = Math.floor(litersFiltered / 5);
  const plasticSaved = (bottlesAvoided * 0.04).toFixed(1); // ~40g per bottle

  return (
    <div className="min-h-screen bg-[#030303]">
      <PortalHeader title="Mi Perfil" />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-light text-white font-['Outfit']">Mi Perfil</h1>
          <p className="text-sm text-zinc-500 mt-1 font-['Manrope']">Gestiona tu información personal</p>
        </div>

        {/* Profile Card */}
        <div className="p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-white/[0.05] to-white/[0.02] border border-white/10 mb-6">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-sky-500/20 to-sky-500/5 border border-sky-500/30 flex items-center justify-center">
              <User className="w-10 h-10 text-sky-400" />
            </div>
            <div className="text-center sm:text-left flex-1">
              <h2 className="text-xl sm:text-2xl font-medium text-white font-['Outfit']">
                {profile?.name || user?.name}
              </h2>
              <p className="text-sm text-zinc-400 mt-1 font-['Manrope']">{profile?.email || user?.email}</p>
              <div className="flex flex-wrap justify-center sm:justify-start gap-2 mt-3">
                <span className="px-3 py-1 rounded-full bg-white/10 text-xs text-zinc-300 flex items-center gap-1.5">
                  <Star className="w-3 h-3 text-amber-400" />
                  Cliente desde {new Date(profile?.customerSince).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
                </span>
                <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-xs text-emerald-400">
                  {profile?.status === 'active' ? 'Cuenta activa' : 'Cuenta inactiva'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Impact Panel - Emotional Metrics */}
        <div className="mb-6 p-5 rounded-2xl bg-gradient-to-br from-sky-500/[0.08] to-emerald-500/[0.05] border border-sky-500/20">
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-5 h-5 text-sky-400" />
            <h3 className="text-sm font-medium text-white font-['Outfit']">Tu impacto Manklar</h3>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <Droplets className="w-5 h-5 text-sky-400 mx-auto mb-1" />
              <p className="text-xl font-light text-white font-['Outfit']">{litersFiltered.toLocaleString()}L</p>
              <p className="text-xs text-zinc-500 font-['Manrope']">Litros filtrados</p>
            </div>
            <div className="text-center">
              <Leaf className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
              <p className="text-xl font-light text-white font-['Outfit']">{bottlesAvoided.toLocaleString()}</p>
              <p className="text-xs text-zinc-500 font-['Manrope']">Botellas evitadas</p>
            </div>
            <div className="text-center">
              <Shield className="w-5 h-5 text-teal-400 mx-auto mb-1" />
              <p className="text-xl font-light text-white font-['Outfit']">{plasticSaved}kg</p>
              <p className="text-xs text-zinc-500 font-['Manrope']">Plástico ahorrado</p>
            </div>
          </div>
        </div>

        {/* Customer Number */}
        <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-zinc-500" />
              <div>
                <p className="text-xs text-zinc-500 font-['Manrope']">Número de cliente</p>
                <p className="text-sm font-mono text-white">{profile?.customerNumber}</p>
              </div>
            </div>
            <span className="px-2 py-1 rounded-md bg-white/5 text-xs text-zinc-400 capitalize">
              {profile?.clientType === 'hogar' ? 'Cliente particular' : 'Empresa'}
            </span>
          </div>
        </div>

        {/* Contact Info */}
        <div className="p-6 rounded-xl bg-white/[0.02] border border-white/10 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm uppercase tracking-wider text-zinc-500 font-['Manrope']">Información de contacto</h3>
            {!editing && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setEditing(true)}
                className="text-zinc-400 hover:text-white"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Editar
              </Button>
            )}
          </div>

          {editing ? (
            <div className="space-y-4">
              <div>
                <label className="block text-xs text-zinc-500 mb-1 font-['Manrope']">Teléfono</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-4 py-3 rounded-lg bg-black border border-white/20 text-white focus:outline-none focus:border-white/40 font-['Manrope']"
                  placeholder="+34 612 345 678"
                />
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1 font-['Manrope']">Dirección</label>
                <input
                  type="text"
                  value={formData.address.street}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    address: { ...prev.address, street: e.target.value }
                  }))}
                  className="w-full px-4 py-3 rounded-lg bg-black border border-white/20 text-white focus:outline-none focus:border-white/40 font-['Manrope']"
                  placeholder="Calle Gran Vía 28"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-zinc-500 mb-1 font-['Manrope']">Ciudad</label>
                  <input
                    type="text"
                    value={formData.address.city}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      address: { ...prev.address, city: e.target.value }
                    }))}
                    className="w-full px-4 py-3 rounded-lg bg-black border border-white/20 text-white focus:outline-none focus:border-white/40 font-['Manrope']"
                    placeholder="Madrid"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1 font-['Manrope']">Código Postal</label>
                  <input
                    type="text"
                    value={formData.address.zipCode}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      address: { ...prev.address, zipCode: e.target.value }
                    }))}
                    className="w-full px-4 py-3 rounded-lg bg-black border border-white/20 text-white focus:outline-none focus:border-white/40 font-['Manrope']"
                    placeholder="28013"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button 
                  onClick={handleSave}
                  className="flex-1 bg-white text-black hover:bg-zinc-200 rounded-xl"
                >
                  Guardar cambios
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setEditing(false);
                    setFormData({
                      phone: profile.phone,
                      address: { ...profile.address }
                    });
                  }}
                  className="border-white/20 text-white hover:bg-white/10 rounded-xl"
                >
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3 py-3 border-b border-white/5">
                <Mail className="w-5 h-5 text-zinc-500" />
                <div>
                  <p className="text-xs text-zinc-500 font-['Manrope']">Email</p>
                  <p className="text-sm text-white font-['Manrope']">{profile?.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 py-3 border-b border-white/5">
                <Phone className="w-5 h-5 text-zinc-500" />
                <div>
                  <p className="text-xs text-zinc-500 font-['Manrope']">Teléfono</p>
                  <p className="text-sm text-white font-['Manrope']">{profile?.phone}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 py-3">
                <MapPin className="w-5 h-5 text-zinc-500 mt-0.5" />
                <div>
                  <p className="text-xs text-zinc-500 font-['Manrope']">Dirección</p>
                  <p className="text-sm text-white font-['Manrope']">
                    {profile?.address?.street}<br />
                    {profile?.address?.zipCode} {profile?.address?.city}<br />
                    {profile?.address?.country}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Seguridad de la cuenta */}
        <SecuritySettings />

        {/* Account Actions - Only Logout (removed fake password change) */}
        <div className="space-y-3">
          <Button 
            variant="outline"
            onClick={handleLogout}
            className="w-full h-12 justify-start border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-xl"
            data-testid="profile-logout-btn"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Cerrar sesión
          </Button>
        </div>

        {/* Legal Links */}
        <div className="mt-8 text-center">
          <p className="text-xs text-zinc-500">
            Al usar Manklar aceptas nuestros{' '}
            <a href="#" className="text-zinc-400 hover:text-white underline">términos de servicio</a>
            {' '}y{' '}
            <a href="#" className="text-zinc-400 hover:text-white underline">política de privacidad</a>
          </p>
        </div>
      </main>

      {/* Scroll to Top Button */}
      <ScrollToTop />
    </div>
  );
}
